import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useProfiles } from "../profiles/ProfileContext";
import { Button } from "../components/ui/Button";
import { PageHeader, Panel } from "../components/Surface";
import { ListRow } from "../components/ListTable";
import { Results, ItemCard } from "../components/Paged";
import { PROFILE_TYPE_LABELS } from "../lib/profiles";
import { myApplications, type JobApplication } from "../lib/jobs";
import { confirmPlacement } from "../lib/placements";

export function MyApplications() {
  const { active } = useProfiles();
  const isDriver = active?.type === "driver";
  const [apps, setApps] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmBusy, setConfirmBusy] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!active || !isDriver) {
      setLoading(false);
      return;
    }
    myApplications(active.id).then((r) => setApps(r.applications)).finally(() => setLoading(false));
  }, [active, isDriver]);

  const accepted = apps.filter((a) => a.status === "accepted" && !confirmed.has(a.id));

  async function confirmHire(a: JobApplication) {
    setConfirmBusy(a.id);
    try {
      await confirmPlacement(a.id);
      setConfirmed((prev) => new Set(prev).add(a.id));
    } finally {
      setConfirmBusy(null);
    }
  }

  return (
    <div className="grid gap-6">
      <PageHeader
        title={`My applications${active ? ` (${PROFILE_TYPE_LABELS[active.type]})` : ""}`}
        subtitle="Track where every application stands."
        actions={<Link to="/jobs"><Button variant="ghost">Browse jobs</Button></Link>}
      />
      {isDriver && accepted.length > 0 && (
        <Panel title="Hires to confirm">
          <p className="text-sm text-muted">You were accepted. Confirm the placement so the hire is official (no fee is charged to you).</p>
          <ul className="grid gap-2 border-t border-line pt-3">
            {accepted.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="font-mono">{a.title ?? "—"}{a.poster_name ? ` · ${a.poster_name}` : ""}</span>
                <Button disabled={confirmBusy === a.id} onClick={() => confirmHire(a)}>
                  {confirmBusy === a.id ? "Confirming…" : "Confirm placement"}
                </Button>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {!isDriver ? (
        <p className="text-sm text-muted">Switch to a driver profile to see your applications.</p>
      ) : loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : (
        <Results
          items={apps}
          perPage={12}
          defaultView="list"
          listColumns={["Job / Company", "Status", "Applied"]}
          gridClass="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
          empty={<p className="font-mono text-sm text-muted">You haven't applied to any jobs yet.</p>}
          renderRow={(a) => (
            <ListRow key={a.id} to={`/jobs/${a.job_id}`} title={a.title ?? "—"} meta={[a.poster_name, a.location].filter(Boolean).join(" · ")} status={a.status} right={new Date(a.created_at).toLocaleDateString()} />
          )}
          renderCard={(a) => (
            <ItemCard key={a.id} to={`/jobs/${a.job_id}`} title={a.title ?? "—"} meta={[a.poster_name, a.location].filter(Boolean).join(" · ")} status={a.status} right={`Applied ${new Date(a.created_at).toLocaleDateString()}`} />
          )}
        />
      )}
    </div>
  );
}
